import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchModels, subscribeModels, type ModelsResponse } from '../service/orchestrator-client';

export interface ModelsState {
  /** The `/models` response in use; null until a read has answered. */
  models: ModelsResponse | null;
  /** A fresh read has not answered: the control's Thinking row is pending. */
  checking: boolean;
  /**
   * Reads `/models` again, bypassing the held response: a 200 replaces it, and a failure leaves
   * it in use.
   */
  refresh: () => void;
}

/**
 * `GET /api/v1/models` for the composer's control: null until it answers (one held response
 * for every reader), then the response, an empty one on failure. It follows every read that
 * answers 200, a send's among them, and `refresh` reads again.
 */
export function useModels(): ModelsState {
  const [models, setModels] = useState<ModelsResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const alive = useRef(true);
  const readSeq = useRef(0);

  useEffect(() => {
    alive.current = true;
    void fetchModels().then((res) => {
      if (alive.current) setModels(res);
    });
    const unsubscribe = subscribeModels((res) => {
      if (alive.current) setModels(res);
    });
    return () => {
      alive.current = false;
      unsubscribe();
    };
  }, []);

  const refresh = useCallback(() => {
    const seq = ++readSeq.current;
    setChecking(true);
    void fetchModels({ fresh: true }).then((res) => {
      if (!alive.current) return;
      setModels(res);
      if (seq === readSeq.current) setChecking(false);
    });
  }, []);

  return { models, checking, refresh };
}
